<div class="sales-dashboard-container">
  <!-- Dashboard Header -->
  <div class="cyber-scan-header-spectacular">
    <div class="d-flex align-items-center">
      <i class="cyber-header-icon fas fa-chart-line me-3"></i>
      <div>
        <h1 class="h3 mb-0">Sales Lead Dashboard</h1>
        <p class="text-muted mb-0">Real-time lead monitoring and management</p>
      </div>
    </div>
    <div class="d-flex align-items-center gap-3">
      <button class="btn btn-cyber-action" (click)="refreshDashboard()">
        <i class="fas fa-sync-alt me-2"></i>
        Refresh
      </button>
      <div class="last-updated text-muted small">
        Last updated: {{ lastUpdated | date: "short" }}
      </div>
    </div>
  </div>

  <!-- Metrics Overview -->
  @if (metrics) {
    <div class="cyber-metrics-grid mb-4">
      <div class="cyber-metric-card">
        <div class="metric-icon bg-primary">
          <i class="fas fa-users"></i>
        </div>
        <div class="metric-content">
          <h3>{{ metrics.totalLeads | number }}</h3>
          <p>Total Leads</p>
          <span class="metric-trend positive">
            +{{ metrics.todayLeads | number }} today
          </span>
        </div>
      </div>
      <div class="cyber-metric-card">
        <div class="metric-icon bg-danger">
          <i class="fas fa-fire"></i>
        </div>
        <div class="metric-content">
          <h3>{{ metrics.highPriorityLeads | number }}</h3>
          <p>High Priority</p>
          <span class="metric-percentage">
            {{
            (metrics.highPriorityLeads / metrics.totalLeads) * 100
            | number: "1.1-1"
            }}%
          </span>
        </div>
      </div>
      <div class="cyber-metric-card">
        <div class="metric-icon bg-success">
          <i class="fas fa-percentage"></i>
        </div>
        <div class="metric-content">
          <h3>{{ metrics.conversionRate | number: "1.1-1" }}%</h3>
          <p>Conversion Rate</p>
          <span
            class="metric-trend"
            [class.positive]="metrics.conversionRate > 5"
            [class.neutral]="metrics.conversionRate <= 5"
            >
            {{ metrics.conversionRate > 5 ? "+" : ""
            }}{{ metrics.conversionRate | number: "1.1-1" }}%
          </span>
        </div>
      </div>
      <div class="cyber-metric-card">
        <div class="metric-icon bg-warning">
          <i class="fas fa-star"></i>
        </div>
        <div class="metric-content">
          <h3>{{ metrics.averageLeadScore | number: "1.0-0" }}</h3>
          <p>Avg. Lead Score</p>
          <span class="metric-subtext">
            {{ metrics.responseTime | number: "1.1-1" }}h avg response
          </span>
        </div>
      </div>
    </div>
  }

  <!-- Filters Section -->
  <div class="cyber-filters-spectacular mb-4">
    <div class="cyber-card">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label" for="priority-filter">Priority</label>
            <select title="Form select"
              class="form-select"
              id="priority-filter"
              [(ngModel)]="filters.priority"
              (change)="applyFilters()"
              >
              <option value="">All Priorities</option>
              <option value="HIGH">🔥 High</option>
              <option value="MEDIUM">⚡ Medium</option>
              <option value="NORMAL">📋 Normal</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label" for="status-filter">Status</label>
            <select title="Form select"
              class="form-select"
              id="status-filter"
              [(ngModel)]="filters.status"
              (change)="applyFilters()"
              >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="CONTACTED">Contacted</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="CONVERTED">Converted</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label" for="date-range-filter">Date Range</label>
            <select title="Form select"
              class="form-select"
              id="date-range-filter"
              [(ngModel)]="filters.dateRange"
              (change)="applyFilters()"
              >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label" for="search-filter">Search</label>
            <input title="Form input"
              type="text"
              id="search-filter"
              class="form-control"
              placeholder="Email, company, name..."
              [(ngModel)]="filters.searchTerm"
              (keyup.enter)="applyFilters()"
              />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Leads Table -->
  <div class="cyber-data-matrix-spectacular">
    <div class="cyber-vuln-matrix">
      <div class="cyber-table-wrapper">
        <table class="table table-hover">
          <thead>
            <tr>
              <th class="cyber-sortable-header" (click)="sortBy('priority')">
                <i class="cyber-header-icon fas fa-flag me-2"></i>
                Priority
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th class="cyber-sortable-header" (click)="sortBy('leadScore')">
                <i class="cyber-header-icon fas fa-star me-2"></i>
                Score
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th
                class="cyber-sortable-header"
                (click)="sortBy('registrationDate')"
                >
                <i class="cyber-header-icon fas fa-user me-2"></i>
                Lead Info
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th class="cyber-sortable-header" (click)="sortBy('company')">
                <i class="cyber-header-icon fas fa-building me-2"></i>
                Company
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th
                class="cyber-sortable-header"
                (click)="sortBy('registrationPath')"
                >
                <i class="cyber-header-icon fas fa-route me-2"></i>
                Source
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th class="cyber-sortable-header" (click)="sortBy('status')">
                <i class="cyber-header-icon fas fa-flag me-2"></i>
                Status
                <i class="fas fa-sort ms-2"></i>
              </th>
              <th>
                <i class="cyber-header-icon fas fa-tools me-2"></i>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            @for (lead of pagedLeads; track trackByLeadId($index, lead)) {
              <tr
                class="cyber-vuln-row"
                >
                <td class="cyber-cell-content">
                  <span
                    class="badge"
                  [class]="
                    'bg-' +
                    salesDashboardService.getPriorityColor(lead.priority)
                  "
                    >
                    {{
                    lead.priority === "HIGH"
                    ? "🔥"
                    : lead.priority === "MEDIUM"
                    ? "⚡"
                    : "📋"
                    }}
                    {{ lead.priority }}
                  </span>
                </td>
                <td class="cyber-cell-content">
                  <div class="score-container">
                    <div class="score-circle" [attr.data-score]="lead.leadScore">
                      <span class="score-text">{{ lead.leadScore }}</span>
                    </div>
                    <small class="text-muted d-block">/100</small>
                  </div>
                </td>
                <td class="cyber-cell-content">
                  <div class="lead-info">
                    <strong>{{ lead.firstName }} {{ lead.lastName }}</strong>
                    <br />
                    <small class="text-muted">{{ lead.email }}</small>
                    <br />
                    <small class="text-muted">{{
                      salesDashboardService.getTimeSinceRegistration(
                      lead.registrationDate
                      )
                    }}</small>
                  </div>
                </td>
                <td class="cyber-cell-content">
                  <div>
                    <strong>{{ lead.company || "Individual" }}</strong>
                    <br />
                    <span class="badge bg-info">{{ lead.tier }}</span>
                  </div>
                </td>
                <td class="cyber-cell-content">
                  <span class="badge bg-secondary">
                    {{ lead.registrationPath.replace("-", " ") | titlecase }}
                  </span>
                </td>
                <td class="cyber-cell-content">
                  <div class="dropdown">
                    <button
                      class="btn btn-sm dropdown-toggle"
                    [class]="
                      'btn-outline-' +
                      salesDashboardService.getStatusColor(lead.status)
                    "
                      type="button"
                      [id]="'statusDropdown' + lead.id"
                      data-bs-toggle="dropdown"
                      >
                      {{ lead.status }}
                    </button>
                    <ul
                      class="dropdown-menu"
                      [attr.aria-labelledby]="'statusDropdown' + lead.id"
                      >
                      <li>
                        <a
                          class="dropdown-item"
                          href="#"
                          (click)="updateLeadStatus(lead, 'NEW', $event)"
                          >New</a
                          >
                        </li>
                        <li>
                          <a
                            class="dropdown-item"
                            href="#"
                            (click)="updateLeadStatus(lead, 'CONTACTED', $event)"
                            >Contacted</a
                            >
                          </li>
                          <li>
                            <a
                              class="dropdown-item"
                              href="#"
                              (click)="updateLeadStatus(lead, 'QUALIFIED', $event)"
                              >Qualified</a
                              >
                            </li>
                            <li>
                              <a
                                class="dropdown-item"
                                href="#"
                                (click)="updateLeadStatus(lead, 'CONVERTED', $event)"
                                >Converted</a
                                >
                              </li>
                              <li>
                                <a
                                  class="dropdown-item"
                                  href="#"
                                  (click)="updateLeadStatus(lead, 'CLOSED', $event)"
                                  >Closed</a
                                  >
                                </li>
                              </ul>
                            </div>
                          </td>
                          <td class="cyber-cell-content">
                            <div class="cyber-action-buttons">
                              <button
                    title="{{
                      salesDashboardService.getRecommendedAction(lead)
                    }}"
                                class="btn btn-sm btn-cyber-action"
                                (click)="viewLeadDetails(lead)"
                                >
                                <i class="fas fa-eye"></i>
                              </button>
                              <button
                                class="btn btn-sm btn-cyber-action"
                                title="Send Email"
                                (click)="sendFollowUpEmail(lead)"
                                >
                                <i class="fas fa-envelope"></i>
                              </button>
                              <button
                                class="btn btn-sm btn-cyber-action"
                                title="Add Note"
                                (click)="addNote(lead)"
                                >
                                <i class="fas fa-sticky-note"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>

                  <!-- Empty State -->
                  @if (leads.length === 0) {
                    <div class="cyber-empty-state">
                      <i class="fas fa-users-slash fa-3x text-muted mb-3"></i>
                      <h4>No leads found</h4>
                      <p class="text-muted">
                        No leads match your current filters. Try adjusting your search
                        criteria.
                      </p>
                    </div>
                  }
                </div>

                <!-- Pagination -->
                @if (totalLeads > pageSize) {
                  <div class="cyber-pagination-container">
                    <nav aria-label="Leads pagination">
                      <ul class="cyber-pagination">
                        <li class="page-item" [class.disabled]="currentPage === 1">
                          <a
                            title="Previous Page"
                            class="cyber-page-link"
                            href="#"
                            (click)="changePage(currentPage - 1, $event)"
                            >
                            <i class="fas fa-chevron-left"></i>
                          </a>
                        </li>
                        @for (page of getPageNumbers(); track page) {
                          <li
                            class="page-item"
                            [class.active]="page === currentPage"
                            >
                            <a
                              class="cyber-page-link"
                              href="#"
                              (click)="changePage(page, $event)"
                              >{{ page }}</a
                              >
                            </li>
                          }
                          <li class="page-item" [class.disabled]="currentPage === totalPages">
                            <a
                              title="Next Page"
                              class="cyber-page-link"
                              href="#"
                              (click)="changePage(currentPage + 1, $event)"
                              >
                              <i class="fas fa-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                      <div class="cyber-pagination-info">
                        Showing {{ (currentPage - 1) * pageSize + 1 }} to
                        {{ Math.min(currentPage * pageSize, totalLeads) }} of
                        {{ totalLeads }} leads
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- Lead Details Modal -->
            @if (selectedLead) {
              <div
                class="modal fade"
                id="leadDetailsModal"
                tabindex="-1"
                >
                <div class="modal-dialog modal-lg">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title">
                        Lead Details: {{ selectedLead.firstName }} {{ selectedLead.lastName }}
                      </h5>
                      <button
                        title="Close Modal"
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                      ></button>
                    </div>
                    <div class="modal-body">
                      <!-- Lead details content would go here -->
                      <div class="row">
                        <div class="col-md-6">
                          <h6>Contact Information</h6>
                          <p><strong>Email:</strong> {{ selectedLead.email }}</p>
                          <p><strong>Company:</strong> {{ selectedLead.company }}</p>
                          <p><strong>Tier:</strong> {{ selectedLead.tier }}</p>
                        </div>
                        <div class="col-md-6">
                          <h6>Lead Metrics</h6>
                          <p><strong>Score:</strong> {{ selectedLead.leadScore }}/100</p>
                          <p><strong>Priority:</strong> {{ selectedLead.priority }}</p>
                          <p><strong>Status:</strong> {{ selectedLead.status }}</p>
                        </div>
                      </div>
                      <div class="mt-3">
                        <h6>Recommended Action</h6>
                        <div class="alert alert-info">
                          {{ salesDashboardService.getRecommendedAction(selectedLead) }}
                        </div>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                        Close
                      </button>
                      <button
                        type="button"
                        class="btn btn-primary"
                        (click)="sendFollowUpEmail(selectedLead)"
                        >
                        Send Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            }
